import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// ── Eager: small public + auth surface (always needed) ─────────────────
import MarketingLayout      from '@/components/marketing/MarketingLayout.jsx';
import Login                from '@/pages/auth/Login.jsx';
import Register             from '@/pages/auth/Register.jsx';
import VerifyOtp            from '@/pages/auth/VerifyOtp.jsx';
import ForgotPassword       from '@/pages/auth/ForgotPassword.jsx';
import ResetPassword        from '@/pages/auth/ResetPassword.jsx';
import ProtectedRoute       from '@/components/common/ProtectedRoute.jsx';
import Layout, { RoleLayout } from '@/components/common/Layout.jsx';
import * as R                from '@/config/roles.js';

// ── Eager: marketing pages (public, light, indexed by crawlers) ────────
import Home                 from '@/pages/marketing/Home.jsx';
import About                from '@/pages/marketing/About.jsx';
import SubsystemsOverview   from '@/pages/marketing/SubsystemsOverview.jsx';
import RERRPage             from '@/pages/marketing/subsystems/RERR.jsx';
import IFPSPage             from '@/pages/marketing/subsystems/IFPS.jsx';
import FIRLPage             from '@/pages/marketing/subsystems/FIRL.jsx';
import VERISPage            from '@/pages/marketing/subsystems/VERIS.jsx';
import ForCitizens          from '@/pages/marketing/ForCitizens.jsx';
import ForAgencies          from '@/pages/marketing/ForAgencies.jsx';
import ForPartners          from '@/pages/marketing/ForPartners.jsx';
import Security             from '@/pages/marketing/Security.jsx';
import Privacy              from '@/pages/marketing/Privacy.jsx';
import Terms                from '@/pages/marketing/Terms.jsx';
import Contact              from '@/pages/marketing/Contact.jsx';

// ── Lazy: dashboard surface, chunked per subsystem ─────────────────────
// React.lazy() + Vite's manualChunks (in vite.config.js) groups pages
// from the same subsystem directory into one shared chunk so e.g. an
// IFPS supervisor downloads one ifps-* chunk for all IFPS pages, not
// one per page.

// Shared across every signed-in role
const AccountSettings       = lazy(() => import('@/pages/dashboard/AccountSettings.jsx'));

// Per-role homes
const CitizenHome           = lazy(() => import('@/pages/dashboard/CitizenHome.jsx'));
const AgencyHome            = lazy(() => import('@/pages/dashboard/AgencyHome.jsx'));
const IfpsHome              = lazy(() => import('@/pages/dashboard/IfpsHome.jsx'));
const FirlHome              = lazy(() => import('@/pages/dashboard/FirlHome.jsx'));
const VerisHome             = lazy(() => import('@/pages/dashboard/VerisHome.jsx'));
const HubHome               = lazy(() => import('@/pages/dashboard/HubHome.jsx'));

// RERR — citizen
const SubmitReport          = lazy(() => import('@/pages/dashboard/citizen/SubmitReport.jsx'));
const MyReports             = lazy(() => import('@/pages/dashboard/citizen/MyReports.jsx'));
const ReportDetail          = lazy(() => import('@/pages/dashboard/citizen/ReportDetail.jsx'));
const ChallengeCenter       = lazy(() => import('@/pages/dashboard/citizen/ChallengeCenter.jsx'));
const IfpsEnrollment        = lazy(() => import('@/pages/dashboard/citizen/IfpsEnrollment.jsx'));
const CitizenBolos          = lazy(() => import('@/pages/dashboard/citizen/CitizenBolos.jsx'));
const CitizenCameras        = lazy(() => import('@/pages/dashboard/citizen/CitizenCameras.jsx'));
const CitizenVehicles       = lazy(() => import('@/pages/dashboard/citizen/CitizenVehicles.jsx'));

// RERR — agency
const DispatchBoard         = lazy(() => import('@/pages/dashboard/agency/DispatchBoard.jsx'));
const BoloManager           = lazy(() => import('@/pages/dashboard/agency/BoloManager.jsx'));
const AuditViewer           = lazy(() => import('@/pages/dashboard/agency/AuditViewer.jsx'));
const JudicialOfficer       = lazy(() => import('@/pages/dashboard/agency/JudicialOfficer.jsx'));
const Manifests             = lazy(() => import('@/pages/dashboard/agency/Manifests.jsx'));
const FieldUnits            = lazy(() => import('@/pages/dashboard/agency/FieldUnits.jsx'));

// IFPS deep
const IfpsChallengeCenter   = lazy(() => import('@/pages/dashboard/ifps/ChallengeCenter.jsx'));
const IdentityStates        = lazy(() => import('@/pages/dashboard/ifps/IdentityStates.jsx'));
const DisputeManager        = lazy(() => import('@/pages/dashboard/ifps/DisputeManager.jsx'));
const EscalationBoard       = lazy(() => import('@/pages/dashboard/ifps/EscalationBoard.jsx'));
const AdjudicationQueue     = lazy(() => import('@/pages/dashboard/ifps/AdjudicationQueue.jsx'));
const InstitutionalPartners = lazy(() => import('@/pages/dashboard/ifps/InstitutionalPartners.jsx'));
const TransactionMonitor    = lazy(() => import('@/pages/dashboard/ifps/TransactionMonitor.jsx'));
const InstitutionSignals    = lazy(() => import('@/pages/dashboard/ifps/InstitutionSignals.jsx'));
const InstitutionChallenges = lazy(() => import('@/pages/dashboard/ifps/InstitutionChallenges.jsx'));

// FIRL deep
const FfoManager              = lazy(() => import('@/pages/dashboard/firl/FfoManager.jsx'));
const GateMonitor             = lazy(() => import('@/pages/dashboard/firl/GateMonitor.jsx'));
const AnomalyBoard            = lazy(() => import('@/pages/dashboard/firl/AnomalyBoard.jsx'));
const EnforcementBoard        = lazy(() => import('@/pages/dashboard/firl/EnforcementBoard.jsx'));
const RecipientManager        = lazy(() => import('@/pages/dashboard/firl/RecipientManager.jsx'));
const AuthorityChainViewer    = lazy(() => import('@/pages/dashboard/firl/AuthorityChainViewer.jsx'));
const SeverityLadderDashboard = lazy(() => import('@/pages/dashboard/firl/SeverityLadderDashboard.jsx'));

// VERIS deep
const RegistrationQueue     = lazy(() => import('@/pages/dashboard/veris/RegistrationQueue.jsx'));
const DedupMatches          = lazy(() => import('@/pages/dashboard/veris/DedupMatches.jsx'));
const EligibilityStates     = lazy(() => import('@/pages/dashboard/veris/EligibilityStates.jsx'));
const VerisDisputes         = lazy(() => import('@/pages/dashboard/veris/VerisDisputes.jsx'));
const DualControlBoard      = lazy(() => import('@/pages/dashboard/veris/DualControlBoard.jsx'));
const PropagationEvents     = lazy(() => import('@/pages/dashboard/veris/PropagationEvents.jsx'));

// Vision Layer (P6) — admin oversight + agency-operated sources
const ThreatBundles         = lazy(() => import('@/pages/dashboard/vision/ThreatBundles.jsx'));
const LiveStreamOversight   = lazy(() => import('@/pages/dashboard/vision/LiveStreamOversight.jsx'));
const SensorSources         = lazy(() => import('@/pages/dashboard/vision/SensorSources.jsx'));
const FleetTelematics       = lazy(() => import('@/pages/dashboard/vision/FleetTelematics.jsx'));

// Hub deep
const HubAudit              = lazy(() => import('@/pages/dashboard/hub/HubAudit.jsx'));
const HubReports            = lazy(() => import('@/pages/dashboard/hub/HubReports.jsx'));
const HubRouting            = lazy(() => import('@/pages/dashboard/hub/HubRouting.jsx'));
const AgencyRegistry        = lazy(() => import('@/pages/dashboard/hub/AgencyRegistry.jsx'));
const CbimAdminConsole      = lazy(() => import('@/pages/dashboard/hub/CbimAdminConsole.jsx'));
const NationalSecurityAlerts = lazy(() => import('@/pages/dashboard/hub/NationalSecurityAlerts.jsx'));
const PmicsAdmin            = lazy(() => import('@/pages/dashboard/hub/PmicsAdmin.jsx'));
const OsintAdmin            = lazy(() => import('@/pages/dashboard/hub/OsintAdmin.jsx'));
const CsdrAdmin             = lazy(() => import('@/pages/dashboard/hub/CsdrAdmin.jsx'));
const CdmsAdmin             = lazy(() => import('@/pages/dashboard/hub/CdmsAdmin.jsx'));
const FeatureRoadmap        = lazy(() => import('@/pages/dashboard/hub/FeatureRoadmap.jsx'));
const PlatformSettings      = lazy(() => import('@/pages/dashboard/hub/PlatformSettings.jsx'));

// Judicial reviewer
const JudicialReviewer      = lazy(() => import('@/pages/dashboard/judicial/JudicialReviewer.jsx'));

// CAD (Phase 10 #6)
const CadDispatchMap        = lazy(() => import('@/pages/dashboard/agency/CadDispatchMap.jsx'));

// Shell-entry lists: who may load at least one page inside each Layout.
// Individual pages are gated more tightly below, from the same constants.
const CITIZEN_ROLES  = R.CITIZEN;
// Was widened to hub_admin and inspector_general, but the API's agency block
// accepts neither: the controller demands an agency membership and aborts
// without one, so both landed on a shell of permissions errors.
const AGENCY_ROLES   = R.AGENCY_ENTRY;
const IFPS_ROLES     = R.IFPS_ENTRY;
// Mirrors the API gate on /ifps/admin/* exactly (role:ifps_supervisor|hub_admin).
// Deliberately narrower than IFPS_ROLES: institutional_partner belongs to the
// partner console only, and inspector_general is not on the admin block either
// — showing an operator surface to a role the endpoint rejects produces a page
// whose entire content is a permissions error.
const IFPS_OPERATOR_ROLES = R.IFPS_OPERATOR;
const FIRL_ROLES     = R.FIRL_ENTRY;
// inspector_general appears in no VERIS gate at all, so it is not here either.
const VERIS_ROLES    = R.VERIS_ENTRY;
/*
| The admin hierarchy, mirroring App\Resolvers\NacisPlatformRoleResolver:
|
|   platform_admin  top tier, and the ONLY role the backend accepts for
|                   /api/platform/* (hub_admin and super_admin are explicitly
|                   NOT mapped into that tier)
|   super_admin     one tier below — broad application-data authority, but
|                   deliberately no platform-config capability
|   hub_admin       subsystem operator, outside the platform tier entirely
|
| Both admin tiers are routed to /hub by ROLE_HOMES, so both must be admitted
| here — omitting them sent every platform_admin straight from login to the
| Forbidden screen with nowhere else to go.
*/
const ADMIN_TIER_ROLES = ['platform_admin','super_admin'];

const HUB_ROLES      = ['hub_admin','inspector_general','judge','magistrate','prosecutor', ...ADMIN_TIER_ROLES];
const JUDICIAL_ROLES = R.JUDICIAL_REVIEWER;
// PMICS / CSDR / CDMS all gate on ifps_supervisor|hub_admin and nothing else.
const REGISTRY_ROLES = R.REGISTRY;

/*
| These two were previously one list, which is what made the settings page
| unusable: the roadmap is a static internal page, whereas platform settings
| is backed by an API that admits platform_admin and nothing else. Gating them
| together meant the only role that could open the page was the one the API
| would reject, and the role that could use the API could not open the page.
*/
const ADMIN_TOOLS_ROLES      = ['hub_admin', ...ADMIN_TIER_ROLES];
const PLATFORM_SETTINGS_ROLES = R.PLATFORM_ADMIN;

/** Lightweight chunk-loading fallback shown inside the Layout shell. */
function PageFallback() {
  return (
    <div className="flex h-full min-h-[60vh] items-center justify-center">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <span className="h-3 w-3 animate-pulse rounded-full bg-sky-500" aria-hidden />
        Loading…
      </div>
    </div>
  );
}

/** Wraps a route subtree in <Suspense> so a route-level chunk fetch
 *  doesn't reveal a blank page while the JS arrives. */
function Lazy({ children }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      {/* Public marketing site (eager) */}
      <Route element={<MarketingLayout />}>
        <Route path="/"                 element={<Home />} />
        <Route path="/about"            element={<About />} />
        <Route path="/subsystems"       element={<SubsystemsOverview />} />
        <Route path="/subsystems/rerr"  element={<RERRPage />} />
        <Route path="/subsystems/ifps"  element={<IFPSPage />} />
        <Route path="/subsystems/firl"  element={<FIRLPage />} />
        <Route path="/subsystems/veris" element={<VERISPage />} />
        <Route path="/for-citizens"     element={<ForCitizens />} />
        <Route path="/for-agencies"     element={<ForAgencies />} />
        <Route path="/for-partners"     element={<ForPartners />} />
        <Route path="/security"         element={<Security />} />
        <Route path="/privacy"          element={<Privacy />} />
        <Route path="/terms"            element={<Terms />} />
        <Route path="/contact"          element={<Contact />} />
      </Route>

      {/* Auth (eager) */}
      <Route path="/login"            element={<Login />} />
      <Route path="/register"         element={<Register />} />
      <Route path="/verify-otp"       element={<VerifyOtp />} />
      <Route path="/forgot-password"  element={<ForgotPassword />} />
      <Route path="/reset-password"   element={<ResetPassword />} />

      {/* Account settings — every authenticated role, no role gate. The shell
          follows the signed-in user rather than the route. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<RoleLayout />}>
          <Route path="/account" element={<Lazy><AccountSettings /></Lazy>} />
        </Route>
      </Route>

      {/* Citizen dashboard */}
      <Route element={<ProtectedRoute roles={CITIZEN_ROLES} />}>
        <Route element={<Layout variant="citizen" />}>
          <Route path="/citizen"                 element={<Lazy><CitizenHome /></Lazy>} />
          <Route path="/citizen/submit"          element={<Lazy><SubmitReport /></Lazy>} />
          <Route path="/citizen/reports"         element={<Lazy><MyReports /></Lazy>} />
          <Route path="/citizen/reports/:id"     element={<Lazy><ReportDetail /></Lazy>} />
          <Route path="/citizen/challenges"      element={<Lazy><ChallengeCenter /></Lazy>} />
          <Route path="/citizen/ifps-enrollment" element={<Lazy><IfpsEnrollment /></Lazy>} />
          <Route path="/citizen/bolos"           element={<Lazy><CitizenBolos /></Lazy>} />
          <Route path="/citizen/cameras"         element={<Lazy><CitizenCameras /></Lazy>} />
          <Route path="/citizen/vehicles"        element={<Lazy><CitizenVehicles /></Lazy>} />
        </Route>
      </Route>

      {/* Agency dashboard */}
      <Route element={<ProtectedRoute roles={AGENCY_ROLES} />}>
        <Route element={<Layout variant="agency" />}>
          <Route path="/agency"                  element={<Lazy><AgencyHome /></Lazy>} />
          <Route path="/agency/dispatch"         element={<Lazy><DispatchBoard /></Lazy>} />
          <Route path="/agency/bolo"             element={<Lazy><BoloManager /></Lazy>} />
          <Route path="/agency/audit"            element={<Lazy><AuditViewer /></Lazy>} />
          <Route path="/agency/audit/:reportId"  element={<Lazy><AuditViewer /></Lazy>} />
          <Route path="/agency/judicial"         element={<Lazy><JudicialOfficer /></Lazy>} />
          <Route path="/agency/cad"              element={<Lazy><CadDispatchMap /></Lazy>} />
          <Route path="/agency/manifests"        element={<Lazy><Manifests /></Lazy>} />
          <Route path="/agency/units"            element={<Lazy><FieldUnits /></Lazy>} />
          {/* Vision Layer surfaces operated by an agency rather than the hub. */}
          <Route path="/agency/sensors"          element={<Lazy><SensorSources /></Lazy>} />
          <Route path="/agency/fleet"            element={<Lazy><FleetTelematics /></Lazy>} />
        </Route>
      </Route>

      {/* IFPS dashboard */}
      <Route element={<ProtectedRoute roles={IFPS_ROLES} />}>
        <Route element={<Layout variant="ifps" />}>
          {/* Government-operator surfaces, all backed by /ifps/admin/* which
              the API gates on ifps_supervisor|hub_admin. Without this inner
              guard an institutional partner — who is in IFPS_ROLES because
              the partner console lives under the same prefix — could reach
              every one of them by URL and be met with "User does not have
              the right roles" instead of a clean refusal. */}
          <Route element={<ProtectedRoute roles={IFPS_OPERATOR_ROLES} />}>
            <Route path="/ifps"             element={<Lazy><IfpsHome /></Lazy>} />
            <Route path="/ifps/challenges"  element={<Lazy><IfpsChallengeCenter /></Lazy>} />
            <Route path="/ifps/identities"  element={<Lazy><IdentityStates /></Lazy>} />
            <Route path="/ifps/disputes"    element={<Lazy><DisputeManager /></Lazy>} />
            <Route path="/ifps/escalations" element={<Lazy><EscalationBoard /></Lazy>} />
            <Route path="/ifps/queue"       element={<Lazy><AdjudicationQueue /></Lazy>} />
            <Route path="/ifps/partners"    element={<Lazy><InstitutionalPartners /></Lazy>} />
            <Route path="/ifps/transactions" element={<Lazy><TransactionMonitor /></Lazy>} />
          </Route>

          {/* Partner console. The API scopes every response to the caller's
              own institution. */}
          <Route path="/ifps/signals"     element={<Lazy><InstitutionSignals /></Lazy>} />
          <Route path="/ifps/our-challenges" element={<Lazy><InstitutionChallenges /></Lazy>} />
        </Route>
      </Route>

      {/* FIRL dashboard */}
      <Route element={<ProtectedRoute roles={FIRL_ROLES} />}>
        <Route element={<Layout variant="firl" />}>
          {/* Casework. The API accepts program_officer here, but not on
              monitoring or enforcement below — so the split is not cosmetic. */}
          <Route element={<ProtectedRoute roles={R.FIRL_CASEWORK} />}>
            <Route path="/firl/ffos"          element={<Lazy><FfoManager /></Lazy>} />
            <Route path="/firl/gates"         element={<Lazy><GateMonitor /></Lazy>} />
            <Route path="/firl/recipients"    element={<Lazy><RecipientManager /></Lazy>} />
            <Route path="/firl/authority"     element={<Lazy><AuthorityChainViewer /></Lazy>} />
          </Route>
          {/* Monitoring admits inspector_general; casework does not. */}
          <Route element={<ProtectedRoute roles={R.FIRL_MONITORING} />}>
            <Route path="/firl/anomalies"     element={<Lazy><AnomalyBoard /></Lazy>} />
          </Route>
          {/* Narrowest of the three. The dashboard and severity board read
              from enforcement as well as casework, so they belong here. */}
          <Route element={<ProtectedRoute roles={R.FIRL_ENFORCEMENT} />}>
            <Route path="/firl"               element={<Lazy><FirlHome /></Lazy>} />
            <Route path="/firl/enforcement"   element={<Lazy><EnforcementBoard /></Lazy>} />
            <Route path="/firl/severity"      element={<Lazy><SeverityLadderDashboard /></Lazy>} />
          </Route>
        </Route>
      </Route>

      {/* VERIS dashboard */}
      <Route element={<ProtectedRoute roles={VERIS_ROLES} />}>
        <Route element={<Layout variant="veris" />}>
          <Route element={<ProtectedRoute roles={R.VERIS_CASEWORK} />}>
            <Route path="/veris"             element={<Lazy><VerisHome /></Lazy>} />
            <Route path="/veris/queue"       element={<Lazy><RegistrationQueue /></Lazy>} />
            <Route path="/veris/dedup"       element={<Lazy><DedupMatches /></Lazy>} />
            <Route path="/veris/eligibility" element={<Lazy><EligibilityStates /></Lazy>} />
            <Route path="/veris/disputes"    element={<Lazy><VerisDisputes /></Lazy>} />
          </Route>
          {/* Adjudication and administration respectively — the API refuses an
              election official on both. */}
          <Route element={<ProtectedRoute roles={R.VERIS_DUAL_CONTROL} />}>
            <Route path="/veris/dual-control" element={<Lazy><DualControlBoard /></Lazy>} />
          </Route>
          <Route element={<ProtectedRoute roles={R.VERIS_PROPAGATION} />}>
            <Route path="/veris/propagation"  element={<Lazy><PropagationEvents /></Lazy>} />
          </Route>
        </Route>
      </Route>

      {/* Hub dashboard */}
      <Route element={<ProtectedRoute roles={HUB_ROLES} />}>
        <Route element={<Layout variant="hub" />}>
          {/* hub.php and rerr.php's hub block both accept hub_admin alone.
              Every other role in this shell — inspector general, judge,
              platform admin — is refused by the API on these five, so the
              guard says so instead of letting the page render its errors. */}
          <Route element={<ProtectedRoute roles={R.HUB_OVERSIGHT} />}>
            <Route path="/hub"        element={<Lazy><HubHome /></Lazy>} />
            <Route path="/hub/audit"  element={<Lazy><HubAudit /></Lazy>} />
            <Route path="/hub/reports" element={<Lazy><HubReports /></Lazy>} />
            {/* The sidebar has linked to /hub/routing since the hub shell was
                built, but no route existed — the link fell through to the
                catch-all and bounced to the public site. */}
            <Route path="/hub/routing"  element={<Lazy><HubRouting /></Lazy>} />
            <Route path="/hub/agencies" element={<Lazy><AgencyRegistry /></Lazy>} />
          </Route>
          {/* Vision Layer and CBIM oversight — these do admit inspector_general
              and ifps_supervisor alongside hub_admin. */}
          <Route element={<ProtectedRoute roles={R.VISION_ADMIN} />}>
            <Route path="/hub/vision"         element={<Lazy><ThreatBundles /></Lazy>} />
            <Route path="/hub/vision/streams" element={<Lazy><LiveStreamOversight /></Lazy>} />
            <Route path="/hub/cbim"   element={<Lazy><CbimAdminConsole /></Lazy>} />
          </Route>
          <Route element={<ProtectedRoute roles={R.NSA_READ} />}>
            <Route path="/hub/nsa"    element={<Lazy><NationalSecurityAlerts /></Lazy>} />
          </Route>
          <Route element={<ProtectedRoute roles={R.REGISTRY} />}>
            <Route path="/hub/pmics"  element={<Lazy><PmicsAdmin /></Lazy>} />
          </Route>
          <Route element={<ProtectedRoute roles={R.OSINT_READ} />}>
            <Route path="/hub/osint"  element={<Lazy><OsintAdmin /></Lazy>} />
          </Route>
        </Route>
      </Route>

      {/* Judicial reviewer dashboard (judges / magistrates / prosecutors) */}
      <Route element={<ProtectedRoute roles={JUDICIAL_ROLES} />}>
        <Route element={<Layout variant="judicial" />}>
          <Route path="/judicial" element={<Lazy><JudicialReviewer /></Lazy>} />
        </Route>
      </Route>

      {/* CSDR + CDMS reference registries — also accessible to ifps_supervisor */}
      <Route element={<ProtectedRoute roles={REGISTRY_ROLES} />}>
        <Route element={<Layout variant="hub" />}>
          <Route path="/hub/csdr"  element={<Lazy><CsdrAdmin /></Lazy>} />
          <Route path="/hub/cdms"  element={<Lazy><CdmsAdmin /></Lazy>} />
        </Route>
      </Route>

      {/* Internal admin tooling — static, no privileged API behind it. */}
      <Route element={<ProtectedRoute roles={ADMIN_TOOLS_ROLES} />}>
        <Route element={<Layout variant="hub" />}>
          <Route path="/hub/roadmap"  element={<Lazy><FeatureRoadmap /></Lazy>} />
        </Route>
      </Route>

      {/* Platform configuration. Gated to platform_admin to match
          RequirePlatformRole on /api/platform/* exactly — a broader gate here
          would only surface a page whose every request 403s. */}
      <Route element={<ProtectedRoute roles={PLATFORM_SETTINGS_ROLES} />}>
        <Route element={<Layout variant="hub" />}>
          <Route path="/hub/settings" element={<Lazy><PlatformSettings /></Lazy>} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
